/**
 * Push Notification Service for admin-vue
 * Endpoints hit the NestJS chat API (VUE_APP_CHAT_API)
 */

const chatApiBase = () =>
  process.env.VUE_APP_CHAT_API || "https://api-n.nkrdieselparts.com";

function getToken() {
  try {
    return (
      JSON.parse(window.localStorage.getItem("auth.currentUser"))?.token || null
    );
  } catch {
    return null;
  }
}

function authHeaders() {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window;
}

export function getNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export async function requestNotificationPermission() {
  if (!("Notification" in window))
    throw new Error("Notifications not supported");
  return await Notification.requestPermission();
}

export async function getVapidPublicKey() {
  const res = await fetch(`${chatApiBase()}/push/vapid_key`);
  const data = await res.json();
  return data.publicKey;
}

async function getServiceWorkerRegistration() {
  // navigator.serviceWorker.ready waits until a SW is fully active.
  // Using getRegistration() can return a SW still in installing/waiting
  // state, which causes pushManager.subscribe() to throw "storage error".
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error("Service Worker not ready (timeout)")),
        15000
      )
    )
  ]);
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push notifications not supported");

  const permission = await requestNotificationPermission();
  if (permission !== "granted")
    throw new Error("Notification permission denied");

  const registration = await getServiceWorkerRegistration();

  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) await existingSubscription.unsubscribe();

  const vapidPublicKey = await getVapidPublicKey();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });

  const sub = subscription.toJSON();
  const res = await fetch(`${chatApiBase()}/push/subscribe`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      endpoint: sub.endpoint,
      keys: { p256dh: sub.keys.p256dh, auth: sub.keys.auth }
    })
  });
  return res.json();
}

export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await fetch(`${chatApiBase()}/push/unsubscribe`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ endpoint: subscription.endpoint })
    });
    await subscription.unsubscribe();
  }

  return { success: true };
}

export async function isSubscribedToPush() {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null;
}

export function initPushListener(onNewChat) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", event => {
      if (event.data?.type === "NEW_CHAT_NOTIFICATION") {
        onNewChat(event.data.data);
      }
    });
  }
}

export default {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  initPushListener
};
